import React from 'react';
import { 
  Scale, FileWarning, Upload, Trophy, AlertTriangle, 
  UserX, Gavel, CheckCircle2, Info, ShoppingBag, Home as HomeIcon, 
  GraduationCap, Briefcase, ShieldAlert, Coins, MessageSquareWarning 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';

const TermsAndConditions = () => {

  const Section = ({ icon: Icon, title, children, color = "text-indigo-600", bgColor = "bg-indigo-50" }) => (
    <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow mb-8">
      <div className="flex items-center gap-4 mb-6 border-b border-slate-50 pb-4">
        <div className={`p-3 rounded-2xl ${bgColor} ${color}`}>
          <Icon size={26} />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h3>
      </div>
      <div className="text-slate-600 leading-relaxed space-y-4 text-sm md:text-base text-justify">
        {children}
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-slate-50 pt-20 pb-20 px-6 font-sans text-slate-800 overflow-hidden">
      <SEO title="Termini e Condizioni" description="Regole di utilizzo, disclaimer legale e condizioni di TusciaHub." url="/terms" />
      
      <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-sky-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-rose-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>

      <div className="relative z-10 max-w-5xl mx-auto">
        <PageHeader title="Termini e Condizioni" subtitle="Contratto di utilizzo e Disclaimer Legale." breadcrumbs={[{label: 'Termini e Condizioni'}]} />
        
        <div className="mb-10 p-8 bg-slate-900 rounded-[2rem] text-white shadow-xl relative overflow-hidden">
            <div className="relative z-10">
                <h3 className="font-bold text-2xl mb-4 flex items-center gap-3"><Scale className="text-indigo-400" size={32}/> Accettazione Vincolante</h3>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-4">
                    Benvenuto su TusciaHub. L'accesso e l'utilizzo di questo sito web costituiscono l'accettazione espressa e senza riserve dei presenti Termini e Condizioni.
                    Se non accetti una qualsiasi parte di questi termini, ti preghiamo di interrompere immediatamente l'utilizzo del servizio.
                </p>
                <p className="text-xs text-slate-400 italic border-t border-slate-700 pt-4 mt-4">
                    TusciaHub è un progetto indipendente e <strong>NON</strong> è il sito ufficiale dell'Università degli Studi della Tuscia, né è affiliato in alcun modo con essa.
                </p>
            </div>
        </div>

        <Section icon={ShoppingBag} title="1. Mercatino: Vendita Libri, Dispense e Materiale" color="text-orange-600" bgColor="bg-orange-50">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <h4 className="font-bold text-red-700 uppercase text-xs tracking-wider mb-1">Limitazione di Responsabilità</h4>
            <p className="text-red-800 text-sm font-bold">
                TusciaHub è una bacheca di annunci. NON gestiamo pagamenti, NON spediamo merce e NON tratteniamo commissioni.
            </p>
          </div>
          <p>
            La sezione "Mercatino" consente agli studenti di pubblicare annunci per la vendita di libri usati, dispense, appunti e altro materiale didattico.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Transazioni Esterne (Off-Platform):</strong> Qualsiasi accordo di compravendita avviene esclusivamente tra Utente Venditore e Utente Acquirente (es. scambio a mano in università, pagamento via PayPal privato, ecc.). TusciaHub è totalmente estraneo alla transazione.</li>
            <li><strong>Vendita di Dispense/Appunti:</strong> È consentito pubblicizzare la vendita di materiale autoprodotto (appunti personali). L'utente dichiara sotto la propria responsabilità di esserne l'autore e di non violare diritti d'autore altrui (es. non vendere fotocopie di libri).</li>
            <li><strong>Manleva:</strong> L'utente esonera TusciaHub da qualsiasi responsabilità riguardante: mancati pagamenti, materiale non conforme alla descrizione, truffe tra privati, o violazioni fiscali derivanti dall'attività di vendita dell'utente.</li>
          </ul>
        </Section>

        <Section icon={Upload} title="2. Caricamento Materiale (Area Condivisione Gratuita)" color="text-indigo-600" bgColor="bg-indigo-50">
          <p>
            Oltre alla vendita nel Mercatino, gli utenti possono caricare file nella sezione "Condivisione" (Repository). 
            <strong>Caricando un file in questa sezione specifica, l'utente intende condividerlo gratuitamente.</strong>
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Licenza d'uso:</strong> Caricando il file nell'area gratuita, concedi a TusciaHub e agli altri studenti il diritto di visualizzarlo e scaricarlo per fini di studio personale.</li>
            <li><strong>Divieto di Copyright:</strong> È severamente vietato caricare materiale protetto da diritto d'autore (es. scansioni integrali di libri commerciali, slide coperte da copyright del docente senza permesso).</li>
            <li>Ci riserviamo il diritto di rimuovere immediatamente contenuti illegali.</li>
          </ul>
        </Section>

        <Section icon={MessageSquareWarning} title="3. Policy Recensioni e Divieto di Diffamazione" color="text-pink-600" bgColor="bg-pink-50">
          <p>
            TusciaHub permette agli utenti di lasciare recensioni (es. su tutor, venditori o esperienze). La libertà di espressione è garantita, ma deve rimanere nei limiti della legge e della civiltà.
          </p>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2 mb-2">
             <strong className="block text-slate-800 mb-2">È severamente vietato pubblicare:</strong>
             <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                 <li>Contenuti diffamatori che ledono la reputazione di persone (studenti, professori, personale universitario) o aziende.</li>
                 <li>Insulti, ingiurie, minacce o linguaggio d'odio (Hate Speech).</li>
                 <li>Informazioni false o non verificate presentate come fatti.</li>
                 <li>Dati personali altrui senza consenso (Doxing).</li>
             </ul>
          </div>
          <p>
            <strong>Responsabilità Legale:</strong> L'utente è l'unico responsabile, civile e penale, dei contenuti che pubblica. TusciaHub collaborerà con le autorità competenti fornendo i log di accesso (Indirizzo IP) qualora venissero richiesti in seguito a denunce per diffamazione o altri reati.
          </p>
        </Section>

        <Section icon={Coins} title="4. Sistema a Punti e Gamification" color="text-yellow-600" bgColor="bg-yellow-50">
          <p>
            Il sito utilizza un sistema di punti virtuali per incentivare la partecipazione.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4 mb-4">
             <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                 <strong className="block text-slate-800 mb-1">Nessun Valore Economico</strong>
                 <p className="text-xs text-slate-500">I punti sono puramente simbolici. NON possono essere convertiti in denaro reale, criptovalute o beni fisici.</p>
             </div>
             <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                 <strong className="block text-slate-800 mb-1">Revoca dei Punti</strong>
                 <p className="text-xs text-slate-500">I punti guadagnati caricando un file verranno <strong>automaticamente sottratti</strong> se il file viene cancellato.</p>
             </div>
          </div>
          <p>
            <strong>Divieto di Farming:</strong> Qualsiasi tentativo di accumulare punti in modo fraudolento (es. caricamento di file vuoti, spam o doppi) comporterà l'azzeramento del punteggio e il ban permanente.
          </p>
        </Section>

        <Section icon={HomeIcon} title="5. Sezione Affitti e Case" color="text-green-600" bgColor="bg-green-50">
          <p>
            TusciaHub non è un'agenzia immobiliare.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Attenzione alle Truffe:</strong> Consigliamo vivamente di <strong>NON inviare mai denaro</strong> (caparre, anticipi) prima di aver visitato l'immobile di persona e aver firmato un contratto.</li>
            <li>Non siamo responsabili per annunci falsi, immobili inesistenti o controversie tra locatore e conduttore.</li>
          </ul>
        </Section>

        <Section icon={Briefcase} title="6. Offerte di Lavoro e Stage" color="text-sky-600" bgColor="bg-sky-50">
          <p>
            Pubblichiamo offerte di lavoro trovate online o inviate da partner a titolo informativo. Non siamo responsabili per la validità delle offerte né per le condizioni contrattuali proposte dalle aziende terze.
          </p>
        </Section>

        <Section icon={UserX} title="7. Ban e Sospensione Account" color="text-red-600" bgColor="bg-red-50">
          <p>
            Gli amministratori di TusciaHub si riservano il diritto insindacabile di sospendere, bannare o cancellare l'account di qualsiasi utente che:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
             <li>Violi i presenti Termini e Condizioni.</li>
             <li>Utilizzi un linguaggio violento, volgare o diffamatorio.</li>
             <li>Pubblichi spam o materiale pubblicitario non autorizzato.</li>
             <li>Tenti di danneggiare tecnicamente la piattaforma.</li>
          </ul>
        </Section>

        <Section icon={ShieldAlert} title="8. Limitazione di Responsabilità (Generale)" color="text-slate-600" bgColor="bg-slate-100">
          <p>
            Il servizio viene fornito "così com'è".
            <br/><br/>
            TusciaHub e i suoi amministratori <strong>NON</strong> saranno responsabili per danni diretti, indiretti, incidentali o consequenziali derivanti dall'uso del sito, inclusi (ma non limitati a) perdita di dati, errori nel materiale didattico, o controversie tra utenti.
          </p>
        </Section>

        <Section icon={Gavel} title="9. Legge Applicabile e Foro Competente" color="text-slate-600" bgColor="bg-slate-100">
          <p>
            I presenti termini sono regolati dalla legge italiana. Per qualsiasi controversia l'utente accetta la competenza esclusiva del Foro di Viterbo, fatte salve le norme inderogabili a tutela del consumatore.
          </p>
        </Section>

        <div className="mt-12 text-center text-slate-400 text-xs">
            <p>Documento redatto in data {new Date().getFullYear()}. Tutti i diritti riservati a TusciaHub.</p>
        </div>

      </div>
    </div>
  );
};

export default TermsAndConditions;